/**
 * @fileoverview Custom modal component to add or remove items with step-based UI.
 * Includes search and autocomplete functionality for user-friendly item management.
 *
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 */
import React from 'react';
import CustomModal from '../Modal/CustomModal';
import CustomSearch from '../SearchSimples/CustomSearch';
import CustomCardContainer from '../CustomCardContainer';
import CustomListContainer from '../CustomListContainer';
import CustomListItem from '../CustomListItem';
import ButtonIcon from '../buttons/ButtonIcon';
import AddRemoveItemsApp from './AddRemoveItemsApp';
import {PersonChipBlack, TrashOutlineIcon} from '../buttons/IconButtons';
import StepperTabs from '../StepperTabs/StepperTabs';
import StepTab from '../StepperTabs/StepTab';
import { toast } from 'react-toastify';
import CustomAutocompleteSearch from "../CustomAutocompleteSearch";
import {SEARCH_USERS_MODEL} from "../table/Model/TableModel";

/**
 * CustomAddRemoveItemsModal Component
 *
 * A modal with two steps to allow users to add items from a list via autocomplete
 * and remove items from a pre-selected list. Includes validation and visual feedback.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {function} props.itemDataTransform - Function to transform raw item data for rendering.
 * @param {Array} props.list - Available items to add.
 * @param {Array} props.selected - Items already selected.
 * @param {function} props.handleAdd - Callback to handle the addition of items.
 * @param {function} props.handleDelete - Callback to handle deletion of items.
 * @param {function} props.onHide - Function to handle modal close.
 * @returns {JSX.Element} The rendered modal component.
 *
 * @example
 * <CustomAddRemoveItemsModal
 *   itemDataTransform={transformFunction}
 *   list={itemsList}
 *   selected={selectedItems}
 *   handleAdd={handleAddFunction}
 *   handleDelete={handleDeleteFunction}
 *   onHide={closeModalFunction}
 * />
 */

const CustomAddRemoveItemsModal = (props) => {
    const { show, selected, handleAdd, handleDelete, onHide, option, modelId=null, listTransform} = props;
    const {
        t, // Translation function
        steps,
        activeStep,
        setActiveStep,
        remove,
        setRemove,
        totalItems,
        emptyFields,
        setEmptyFields,
        selectedAssignTo,
        setSelectedAssignTo,
        handleRemoveSelected,
        handleStepChange,
        handleSelected
    } = AddRemoveItemsApp({ show });

    return (
        <CustomModal
            {...props}
            title={t('add') + ' / ' + t('remove')}
            size='lg' // Modal size (large)
            buttonConfirm='confirm' // Text on the confirm button
            cancelButton={true} // Show cancel button
            confirmButton={activeStep === 0}
            onHide={() => {
                setEmptyFields(false);
                setSelectedAssignTo(null);
                setRemove('');
                onHide();
            }}
            onConfirm={() => {
                if (totalItems?.length === 0) {
                    toast.warning(t('add_at_least_one_item'));
                    setEmptyFields(true);
                    return;
                }
                setEmptyFields(false);
                handleAdd(totalItems);
            }} // Function to call when the confirm button is clicked
        >
            <StepperTabs
                onlyTab={true}
                disabled={false}
                steps={steps}
                activeStep={activeStep}
                isStepOptional={() => {}}
                setActiveStep={setActiveStep}
                handleStepChange={handleStepChange}
            >
                <StepTab index={0} activeStep={activeStep} normalHeight={true}>
                    <div className={'col-12'}>
                        <CustomAutocompleteSearch
                            id="slt_car_search_for_add"
                            label={t("search")}
                            itemSelected={selectedAssignTo}
                            setItemSelected={setSelectedAssignTo}
                            handleSelected={handleSelected}
                            bodyApi={{...SEARCH_USERS_MODEL, model:option, modelId: modelId }}
                            required={true}
                            emptyFields={emptyFields}
                            reset={true}
                            keyId={"userId"}
                            listTransform={listTransform}
                            Icon={PersonChipBlack}
                        />
                    </div>

                    <div className='col-12 mt-4'>
                        <CustomCardContainer cardContentClass={'h-40vh'} emptyFields={emptyFields}>
                            <CustomListContainer>
                                {totalItems?.map((item, index) => (
                                    <CustomListItem
                                        display={'flex'}
                                        key={index}
                                        text={`${item?.label}`}
                                        actions={
                                            <ButtonIcon
                                                id='btn_car_delete'
                                                Icon={TrashOutlineIcon}
                                                onClick={() => {
                                                    handleRemoveSelected(item);
                                                }}
                                                title={''}
                                                className='btn-background-transparent'
                                                iconProps={'btn-icon-svg-red btn-icon-svg-12px'}
                                            />
                                        }
                                    />
                                ))}
                            </CustomListContainer>
                        </CustomCardContainer>
                    </div>
                </StepTab>
                <StepTab index={1} activeStep={activeStep} normalHeight={true}>
                    <div className={'col-12'}>
                        <CustomSearch
                            id={'txt_car_search_for_remove'}
                            label={t('search')}
                            value={remove}
                            setValue={setRemove}
                            onChange={(e) => setRemove(e.target.value)}
                            onSearchIcon={() => {}}
                            searchFunction={() => {}}
                            onReset={() => {}}
                            disabled={selected?.length === 0}
                            blurOnSelect={true}
                        />
                    </div>
                    <div className={'col-12 ' + (selected?.length === 0 ? '' : 'mt-4')}>
                        <CustomCardContainer cardContentClass={'h-40vh'}>
                            <CustomListContainer>
                                {selected?.filter((element) => {
                                        if (remove === '') {
                                            return element;
                                        } else if (element.label.toLowerCase().includes(remove.toLowerCase())) {
                                            return element;
                                        }
                                    })
                                    .map((item, index) => (
                                        <CustomListItem
                                            display={'flex'}
                                            key={index}
                                            text={`${item?.label}`}
                                            actions={
                                                <ButtonIcon
                                                    id={'btn_car_delete_' + item.id}
                                                    Icon={TrashOutlineIcon}
                                                    onClick={() => {
                                                        handleDelete(item.id);
                                                    }}
                                                    title={''}
                                                    className='btn-background-transparent'
                                                    iconProps={'btn-icon-svg-red btn-icon-svg-12px'}
                                                />
                                            }
                                        />
                                    ))}
                            </CustomListContainer>
                        </CustomCardContainer>
                    </div>
                </StepTab>
            </StepperTabs>
        </CustomModal>
    );
};

export default CustomAddRemoveItemsModal;
